import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Wand2 } from "lucide-react";
import { listEnhancements } from "@/lib/enhanceApi";
import { PREVIEW_ACTIVE, deletePreview, makePreview, previewAudioUrl } from "@/lib/myVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

const sel = "w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white focus:border-cyan-300/50 focus:outline-none";
const lbl = "mb-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/50";
const PITCHES = Array.from({ length: 25 }, (_, i) => i - 12);

function PreviewRow({ voiceId, p, onChanged }) {
  const busy = PREVIEW_ACTIVE.includes(p.status);
  const remove = async () => { try { await deletePreview(voiceId, p.id); onChanged(); } catch (e) { toast.error("Couldn't delete", { description: safeApiError(e) }); } };
  return (
    <li data-testid={`preview-${p.id}`} className="py-3">
      <div className="flex items-center gap-2 text-[13px]">
        <span className="min-w-0 flex-1 truncate">{p.guide.label} <span className="text-white/40">· from {Math.round(p.guide.start_sec)} s{p.pitch ? ` · ${p.pitch > 0 ? "+" : ""}${p.pitch} semitones` : ""}</span></span>
        {busy ? <span className="flex items-center gap-1.5 text-[12px] text-amber-200"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Singing it in your voice…</span> : null}
        <button type="button" data-testid={`preview-delete-${p.id}`} aria-label="Delete preview" disabled={busy} onClick={remove} className="grid h-7 w-7 place-items-center rounded-full text-white/40 hover:text-rose-200 disabled:opacity-30"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      {p.error ? <p className="mt-1 text-[12px] text-rose-200">{p.error}</p> : null}
      {p.status === "done" ? (
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {[["guide", "Guide (what went in)"], ["voice", "In your trained voice"]].map(([w, t]) => (
            <div key={w}><div className="mb-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">{t}</div><audio data-testid={`preview-audio-${w}-${p.id}`} controls preload="none" src={previewAudioUrl(voiceId, p.id, w)} className="h-8 w-full" /></div>
          ))}
        </div>
      ) : null}
    </li>
  );
}

/** Hear a ready version sing 25 seconds of any vocal you pick. */
export default function PreviewPanel({ voiceId, version, clips, connected, onChanged }) {
  const [enhanced, setEnhanced] = useState([]);
  const [guide, setGuide] = useState("");
  const [start, setStart] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { listEnhancements().then((r) => setEnhanced(r.filter((x) => x.versions?.natural))).catch(() => setEnhanced([])); }, []);
  const ready = (clips || []).filter((c) => c.status === "ready");
  const making = version.previews.some((p) => PREVIEW_ACTIVE.includes(p.status));
  const go = async () => {
    const [kind, id] = guide.split(":");
    setBusy(true);
    try { await makePreview(voiceId, version.id, { guide_kind: kind, guide_id: id, start_sec: Number(start) || 0, pitch: Number(pitch) }); onChanged(); }
    catch (e) { toast.error("Couldn't make a preview", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`preview-panel-${version.id}`} className="mt-4 rounded-2xl border border-white/[0.06] bg-black/30 p-4">
      <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
        <div>
          <label className={lbl} htmlFor={`guide-${version.id}`}>Vocal to sing</label>
          <select id={`guide-${version.id}`} data-testid={`preview-guide-${version.id}`} className={sel} value={guide} onChange={(e) => setGuide(e.target.value)}>
            <option value="">Choose a vocal…</option>
            {enhanced.length ? <optgroup label="Enhanced vocals">{enhanced.map((x) => <option key={x.id} value={`enhance:${x.id}`}>{x.title}</option>)}</optgroup> : null}
            {ready.length ? <optgroup label="Training recordings">{ready.map((c) => <option key={c.id} value={`clip:${c.id}`}>{c.name}</option>)}</optgroup> : null}
          </select>
        </div>
        <div>
          <label className={lbl} htmlFor={`start-${version.id}`}>Start at (s)</label>
          <input id={`start-${version.id}`} data-testid={`preview-start-${version.id}`} type="number" min={0} max={900} value={start} onChange={(e) => setStart(e.target.value)} className={sel} />
        </div>
        <div>
          <label className={lbl} htmlFor={`pitch-${version.id}`}>Pitch shift</label>
          <select id={`pitch-${version.id}`} data-testid={`preview-pitch-${version.id}`} className={sel} value={pitch} onChange={(e) => setPitch(e.target.value)}>
            {PITCHES.map((n) => <option key={n} value={n}>{n === 0 ? "None" : `${n > 0 ? "+" : ""}${n} semitones`}</option>)}
          </select>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-white/45">25 seconds, a few cents on Replicate. Use −12 when the guide is a female singer an octave above you.</p>
      <button type="button" data-testid={`preview-make-${version.id}`} onClick={go} disabled={!guide || busy || making || !connected}
        className="mt-3 inline-flex items-center gap-2 rounded-full bg-cyan-300 px-4 py-2 text-[12px] font-semibold text-black disabled:opacity-40">
        {busy || making ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Make a preview
      </button>
      {version.previews.length ? <ul className="mt-3 divide-y divide-white/[0.06]">{version.previews.map((p) => <PreviewRow key={p.id} voiceId={voiceId} p={p} onChanged={onChanged} />)}</ul> : null}
    </div>
  );
}
